import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ExternalLink, ArrowRight, Building2, BookOpen, Globe2, Languages } from 'lucide-react';

export const AboutPage: React.FC = () => {
  return (
    <div className="space-y-16 py-12">
      
      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-50 border border-brand-200 text-brand-800 text-[11px] font-mono tracking-wider uppercase font-semibold">
            <Building2 className="w-3.5 h-3.5 text-brand-600" />
            <span>FOR THE TRUTH PRESS PRIVATE LIMITED</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-serif font-bold text-slate-900 tracking-tight">
            Corporate Profile &amp; Mission
          </h1>
          <p className="text-base sm:text-lg font-serif italic text-slate-600">
            Publishing, rights licensing, and nationwide distribution of biblical and theological literature in India.
          </p>
        </div>
      </section>

      {/* 2. Core Corporate Overview & Library Image */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-base sm:text-lg text-slate-800 leading-relaxed font-sans">
            <strong>FOR THE TRUTH PRESS PRIVATE LIMITED</strong> is an Indian publishing company established to produce, license, translate, and distribute faithful Christian literature for churches, seminaries, and readers across India and South Asia.
          </p>
        </div>

        <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-lg border border-slate-200">
          <img
            src="https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&q=80&w=1600"
            alt="For The Truth Press Library & Archive"
            className="w-full h-64 sm:h-80 md:h-96 object-cover"
          />
        </div>
      </section>

      {/* 3. Three Strategic Corporate Pillars */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="text-center space-y-1">
          <span className="text-[11px] font-mono uppercase tracking-widest text-brand-600 font-bold">
            Corporate Objectives
          </span>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
            Strategic Focus
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700 font-mono font-bold">
              <BookOpen className="w-5 h-5" />
            </div>
            <h3 className="font-serif text-base font-bold text-slate-900">
              Sound English Publishing
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Publishing peer-reviewed, theologically sound titles in English, upholding high editorial and typographical standards.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700 font-mono font-bold">
              <Globe2 className="w-5 h-5" />
            </div>
            <h3 className="font-serif text-base font-bold text-slate-900">
              Affordable Pan-India Distribution
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Distributing classical and contemporary international titles at affordable prices through nationwide logistics and online retail.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700 font-mono font-bold">
              <Languages className="w-5 h-5" />
            </div>
            <h3 className="font-serif text-base font-bold text-slate-900">
              Vernacular Translations
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Translating seminal theological works into Hindi, Tamil, Telugu, Malayalam, and other Indian languages with contextual fidelity.
            </p>
          </div>

        </div>
      </section>

      {/* 4. Corporate & Institutional Action */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-10 rounded-2xl bg-slate-950 text-white space-y-5 border border-slate-800">
          <div className="max-w-2xl space-y-2">
            <span className="text-[11px] font-mono uppercase tracking-widest text-brand-400 font-bold">
              Trade &amp; Institutional Partnerships
            </span>
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-100">
              Engage With FOR THE TRUTH PRESS
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Contact our corporate desk for international co-publishing rights, academic bulk orders, or manuscript submissions.
            </p>
          </div>

          <div className="pt-1 flex flex-wrap gap-3 text-xs font-semibold">
            <Link
              to="/contact"
              className="px-5 py-3 rounded-xl bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold transition-all shadow-md inline-flex items-center gap-1.5"
            >
              <span>Corporate Desk</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>

            <Link
              to="/publications"
              className="px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-700 transition-all"
            >
              View 430+ Titles Catalog
            </Link>

            <a
              href="https://forthetruth.in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 transition-all"
            >
              <span>Visit Bookstore (forthetruth.in)</span>
              <ExternalLink className="w-3.5 h-3.5 text-brand-400" />
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};
